import React from 'react'
import { Box, Paper, Typography, Avatar } from '@mui/material'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import PersonIcon from '@mui/icons-material/Person'
import { ConversationSegment } from '@/shared/types/yakurekiCore'
import { customScrollbarStyles } from '@/apps/legacy/shared/styles/scrollbarStyles'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import AudioPlayer from '@/apps/legacy/components/molecules/AudioPlayer'

interface TranscriptionViewerProps {
  conversationSegments: ConversationSegment[]
  audioUrl?: string
  onTabChange?: () => void
  showAudioPlayer?: boolean
  compact?: boolean
}

const TranscriptionViewer: React.FC<TranscriptionViewerProps> = ({
  conversationSegments,
  audioUrl,
  onTabChange,
  showAudioPlayer = true,
  compact = false,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {showAudioPlayer && (
        <AudioPlayer audioUrl={audioUrl} onTabChange={onTabChange} />
      )}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 0, // flexboxの子要素の最小高さをリセット
          ...customScrollbarStyles,
        }}
      >
        {conversationSegments.length > 0 ? (
          <>
            {conversationSegments.map((entry, index) => {
              if (entry.speaker === 'システム') {
                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.75,
                      backgroundColor: colorTokens.grey[200],
                      borderTop: `1px solid ${colorTokens.grey[300]}`,
                      borderBottom: `1px solid ${colorTokens.grey[300]}`,
                      py: compact ? 0.75 : 1.5,
                      px: 2,
                      my: compact ? 1.5 : 3,
                    }}
                  >
                    <SwapHorizIcon
                      sx={{
                        color: colorTokens.text.secondary,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: colorTokens.text.secondary,
                      }}
                    >
                      {entry.content.replace(/[<>]/g, '')}
                    </Typography>
                  </Box>
                )
              }

              const isPharmacist = entry.speaker === '薬剤師'
              const nextEntry = conversationSegments[index + 1]
              const isSameSpeakerAsNext =
                nextEntry && nextEntry.speaker === entry.speaker

              return (
                <Box
                  key={index}
                  sx={[
                    {
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                    },
                    isPharmacist
                      ? {
                          flexDirection: 'row-reverse',
                        }
                      : {
                          flexDirection: 'row',
                        },
                    isSameSpeakerAsNext
                      ? {
                          mb: compact ? 0.5 : 1,
                        }
                      : {
                          mb: compact ? 1 : 2.5,
                        },
                  ]}
                >
                  {/* Avatar */}
                  <Avatar
                    sx={[
                      {
                        width: 32,
                        height: 32,
                      },
                      isPharmacist
                        ? {
                            bgcolor: colorTokens.blue[600],
                          }
                        : {
                            bgcolor: colorTokens.text.secondary,
                          },
                    ]}
                  >
                    {isPharmacist ? <PersonAddIcon /> : <PersonIcon />}
                  </Avatar>
                  {/* Chat bubble */}
                  <Box
                    sx={[
                      {
                        maxWidth: '75%',
                        borderRadius: compact ? 2 : 2.5,
                        px: compact ? 1.5 : 2,
                        py: compact ? 0.75 : 1.5,
                        position: 'relative',
                      },
                      isPharmacist
                        ? {
                            backgroundColor: colorTokens.blue[50],
                          }
                        : {
                            backgroundColor: colorTokens.grey[100],
                          },
                    ]}
                  >
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        lineHeight: 1.5,
                        color: colorTokens.text.primary,
                      }}
                    >
                      {entry.content}
                    </Typography>
                  </Box>
                </Box>
              )
            })}
          </>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: colorTokens.background.subtle,
              border: `1px solid ${colorTokens.border.light}`,
              borderRadius: 2,
              textAlign: 'center',
              minHeight: 120,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: colorTokens.text.secondary,
              }}
            >
              文字起こし結果はありません
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  )
}

export default TranscriptionViewer
